import { Link } from 'react-router-dom';
import { CheckSquare, Zap, Shield, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';

const features = [
    {
        icon: Zap,
        title: 'Fast & Efficient',
        description: 'Create and manage tasks quickly with our intuitive interface.',
    },
    {
        icon: Shield,
        title: 'Secure',
        description: 'Your data is protected with industry-standard security.',
    },
    {
        icon: Clock,
        title: 'Always Available',
        description: 'Access your tasks from anywhere, anytime.',
    },
];

export function Home() {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                <div className="text-center">
                    <div className="flex justify-center mb-8">
                        <div className="bg-primary-600 p-4 rounded-2xl shadow-lg shadow-primary-600/30">
                            <CheckSquare className="w-12 h-12 text-white" />
                        </div>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight">
                        Manage Your Tasks
                        <span className="block text-primary-600">With Ease</span>
                    </h1>
                    <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
                        A simple yet powerful task management application to help you stay organized
                        and productive. Create, track, and complete your tasks effortlessly.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register">
                            <Button size="lg" className="flex items-center gap-2">
                                Get Started Free
                                <ArrowRight className="w-5 h-5" />
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="secondary" size="lg">
                                Sign In
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="bg-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900">Why Choose TaskMaster?</h2>
                        <p className="mt-4 text-gray-600">Everything you need to manage your tasks effectively</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {features.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <div
                                    key={feature.title}
                                    className="text-center p-6 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                                        <Icon className="w-6 h-6 text-primary-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-600">{feature.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-primary-600 py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">
                        Ready to Get Started?
                    </h2>
                    <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
                        Join thousands of users who are already managing their tasks more efficiently.
                    </p>
                    <Link to="/register">
                        <Button
                            variant="secondary"
                            size="lg"
                            className="bg-white text-primary-600 hover:bg-gray-100"
                        >
                            Create Free Account
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p>&copy; {new Date().getFullYear()} TaskMaster. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}